/**
 * The workout summary in the completion card while the compact-feedback switch is on (#4142
 * follow-up, design-docs/workout-completion-summary.md, board "Summary B").
 *
 * Pure and Angular-free, so every number on the card is unit-tested away from the page. It is
 * computed only from the workout's stored sets. The rules and the reasons behind them:
 * `design-docs/workout-completion-summary.md`, "The workout summary".
 */
import { SetType } from '@cf-shared/enums';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { DateTime } from 'luxon';
import { getSetOutcomeMark, getSetProgressionResult, RepRangePathFields, SetOutcomeMark, SetProgressionInput, WeightAndReps } from './set-progress-display';

/** The fields of a stored set the summary reads. Loosely typed: an old or damaged document may hold anything. */
export interface SummarySetFields {
  state?: unknown;
  setType?: unknown;
  setSuccess?: unknown;
  stats?: unknown;
  plannedWeight_kg?: unknown;
  planPathBackup?: unknown;
  deloadDeclined?: unknown;
  noGainOverLastSession?: unknown;
  completedTime_utc?: unknown;
}

/** One exercise of the workout, in workout order. */
export interface SummaryExerciseInput {
  /** the exercise's id, used to match its personal-record row; absent on a damaged entry */
  exerciseId?: string;
  name: string;
  sets: SummarySetFields[];
}

/** One row of the summary: an exercise and its set 1 against last time. */
export interface WorkoutSummaryRow {
  exerciseId?: string;
  name: string;
  /** set 1's mark, the same as on its own row (#4142 review fixes) */
  mark: SetOutcomeMark;
  today: WeightAndReps;
  lastTime?: WeightAndReps;
  /** a working set of this exercise strictly beat its best from every other workout */
  personalRecord: boolean;
}

export interface WorkoutSummary {
  /** sets with state Done */
  sets: number;
  /** Σ total weight × reps over those sets, in kg (body weight included) */
  volume_kg: number;
  /** earliest to latest log time among those sets, whole minutes; absent with fewer than 2 usable times */
  durationMinutes?: number;
  /** exercises marked as a personal record */
  personalRecords: number;
  /** exercises whose set 1 carries the green mark */
  progressedExercises: number;
  rows: WorkoutSummaryRow[];
}

/**
 * Builds the summary. `recordExerciseIds` is the set of exercise ids whose personal record was
 * beaten (from `personalRecordReport`); pass nothing while that lookup is still running, and the
 * summary shows no records yet.
 */
export function buildWorkoutSummary(exercises: SummaryExerciseInput[], recordExerciseIds?: ReadonlySet<string>): WorkoutSummary {
  // only finished sets count; skipped and unfinished ones do not
  const doneSets = exercises.flatMap(exercise => exercise.sets).filter(set => set.state === LogRecordState.Done);
  let volume_kg = 0;
  const times: number[] = [];
  for (const set of doneSets) {
    volume_kg += setVolume_kg(set.stats);
    const millis = readMillis(set.completedTime_utc);
    if (millis !== undefined) {
      times.push(millis);
    }
  }
  const rows: WorkoutSummaryRow[] = [];
  for (const exercise of exercises) {
    const row = setOneRow(exercise, recordExerciseIds);
    if (row) {
      rows.push(row);
    }
  }

  const summary: WorkoutSummary = {
    sets: doneSets.length,
    volume_kg,
    personalRecords: rows.filter(row => row.personalRecord).length,
    progressedExercises: rows.filter(row => row.mark === SetOutcomeMark.Success).length,
    rows,
  };
  // a duration needs two moments to lie between
  if (times.length >= 2) {
    summary.durationMinutes = Math.round((Math.max(...times) - Math.min(...times)) / 60000);
  }
  return summary;
}

// the row for an exercise's set 1, or undefined when set 1 was skipped, has no result, or the
// exercise has no benchmark set at all (its other done sets still count in the totals)
function setOneRow(exercise: SummaryExerciseInput, recordExerciseIds: ReadonlySet<string> | undefined): WorkoutSummaryRow | undefined {
  const setOne = exercise.sets.find(set => set.setType === SetType.Regular1st);
  if (!setOne || setOne.state !== LogRecordState.Done) {
    return undefined;
  }
  const input: SetProgressionInput = {
    setType: SetType.Regular1st,
    setSuccess: setOne.setSuccess,
    stats: setOne.stats,
    plannedWeight_kg: setOne.plannedWeight_kg,
    // a backup that is not an object reads as no backup; its fields are checked one by one inside
    planPathBackup: setOne.planPathBackup && typeof setOne.planPathBackup === 'object' ? setOne.planPathBackup as RepRangePathFields : undefined,
    deloadDeclined: setOne.deloadDeclined,
    noGainOverLastSession: setOne.noGainOverLastSession,
  };
  const result = getSetProgressionResult(input);
  if (!result) {
    return undefined;
  }
  const row: WorkoutSummaryRow = {
    name: exercise.name,
    mark: getSetOutcomeMark(input),
    today: result.done,
    personalRecord: !!exercise.exerciseId && !!recordExerciseIds?.has(exercise.exerciseId),
  };
  if (exercise.exerciseId) {
    row.exerciseId = exercise.exerciseId;
  }
  if (result.lastTime) {
    row.lastTime = result.lastTime;
  }
  return row;
}

// one set's volume: its TOTAL weight (body weight included, so body-weight exercises count and an
// assisted set never goes negative) times its reps. A set from an old document without a usable
// total weight falls back to the added weight; anything unusable counts as nothing
function setVolume_kg(stats: unknown): number {
  if (!stats || typeof stats !== 'object') {
    return 0;
  }
  const record = stats as Record<string, unknown>;
  const reps = finiteOrUndefined(record.reps);
  const weight_kg = finiteOrUndefined(record.weight_net_kg) ?? finiteOrUndefined(record.weight_kg);
  if (reps === undefined || weight_kg === undefined || reps <= 0 || weight_kg <= 0) {
    return 0;
  }
  return weight_kg * reps;
}

// a stored log time as epoch milliseconds, or undefined when it is missing or unreadable
function readMillis(value: unknown): number | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const time = DateTime.fromISO(value);
  return time.isValid ? time.toMillis() : undefined;
}

// a value is usable only when it is a finite number
function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
